/**
 * <nw-header-search-form> hands the inline header search field off to the
 * theme's predictive search modal (rendered globally by snippets/search-modal
 * in theme.liquid).
 *
 * This used to be a plain script that grabbed [data-nw-search-form] via
 * querySelectorAll on 'DOMContentLoaded' / 'shopify:section:load' and marked
 * each input done with a dataset flag so it wouldn't bind twice. That only
 * covers the specific moments those two events fire - any other way this
 * markup ends up in the DOM (a morph, a duplicate render) left the field
 * inert with no error, since the fallback is a silent native form submit.
 * A custom element doesn't have that gap: connectedCallback runs for every
 * instance as soon as the browser connects it, the same way
 * <device-finder-form> (assets/device-finder.js) already stays wired up
 * across this section's re-renders.
 *
 * Opening on click/input rather than focus matters: closing the dialog
 * returns focus to this input, and a focus handler would reopen it forever.
 */
class NwHeaderSearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-nw-search-input]');
    if (!this.input) return;

    this.addEventListener('click', this.handleOpen);
    this.addEventListener('input', this.handleOpen);
    this.addEventListener('submit', this.handleOpen);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleOpen);
    this.removeEventListener('input', this.handleOpen);
    this.removeEventListener('submit', this.handleOpen);
  }

  /** @param {Event} event */
  handleOpen = (event) => {
    this.openModal(event);
  };

  /**
   * @param {Event} event
   */
  async openModal(event) {
    event.preventDefault();

    const modal = /** @type {any} */ (document.getElementById('search-modal'));
    if (!modal || !this.input) return;

    // dialog-component's module script loads deferred, so a fast first
    // click/keystroke can beat it - showDialog only exists once the
    // element has upgraded. Wait for that instead of giving up and
    // silently falling through to a plain form submit, which is what
    // made this look like search wasn't wired up to anything live.
    await customElements.whenDefined('dialog-component');
    if (typeof modal.showDialog !== 'function') return;

    const query = this.input.value;
    modal.showDialog();

    const modalInput = modal.querySelector('input[name="q"]');
    if (modalInput instanceof HTMLInputElement) {
      modalInput.value = query;
      requestAnimationFrame(() => modalInput.focus());
      // Same upgrade race as dialog-component above: predictive-search
      // -component listens for this event itself, so dispatching it
      // before that element has upgraded would be a no-op.
      if (query) {
        await customElements.whenDefined('predictive-search-component');
        modalInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    this.input.value = '';
    this.input.blur();
  }
}

if (!customElements.get('nw-header-search-form')) {
  customElements.define('nw-header-search-form', NwHeaderSearchForm);
}
